// src/pages/client/Agenda.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from 'contexts/AppContext';
import { appointmentsService } from 'infrastructure/firebase';
import { StatusBadge, Button, FilterChip, EmptyState } from 'shared/components/ui';
import { formatCurrency } from 'shared/utils/helpers';
import { useMobile } from 'shared/hooks/useMobile';

const CHAT_ELIGIBLE = ['pending','confirmed','waitingConfirm'];

export default function ClientAgenda() {
  const isMobile = useMobile();
  const { myAppointments: appointments, showToast } = useApp();
  const navigate = useNavigate();

  const _n = new Date();
  const today = `${_n.getFullYear()}-${String(_n.getMonth()+1).padStart(2,'0')}-${String(_n.getDate()).padStart(2,'0')}`;
  const [filter, setFilter] = useState('upcoming');

  const filtered = appointments.filter(a => {
    if (filter === 'upcoming') return a.date >= today && a.status !== 'cancelled';
    if (filter === 'past')     return a.date < today || a.status === 'cancelled';
    return true;
  }).sort((a,b) => {
    if (filter === 'past') return a.date < b.date ? 1 : -1;
    return a.date > b.date ? 1 : a.time > b.time ? 1 : -1;
  });

  const handleCancel = async (appt) => {
    if (!window.confirm('Deseja cancelar este agendamento?')) return;
    try {
      await appointmentsService.cancel(appt.id);
      showToast('Agendamento cancelado.');
    } catch { showToast('Erro ao cancelar agendamento.', 'danger'); }
  };

  const handleConfirm = async (appt) => {
    try {
      await appointmentsService.confirm(appt.id);
      showToast('Presença confirmada! 🎉');
    } catch { showToast('Erro ao confirmar presença.', 'danger'); }
  };

  // Pode confirmar se aguardando confirmação e ainda não passou
  const canConfirm = (appt) => {
    if (appt.status !== 'waitingConfirm' && appt.status !== 'pending') return false;
    if (!appt.date || !appt.time) return true;
    const apptDateTime = new Date(`${appt.date}T${appt.time}:00`);
    return !isNaN(apptDateTime) && apptDateTime > new Date();
  };

  const canCancel = (appt) => {
    const apptDateTime = new Date(`${appt.date}T${appt.time}:00`);
    return apptDateTime > new Date() && appt.status !== 'cancelled';
  };

  return (
    <div style={{ maxWidth: 680, padding: isMobile ? '0' : undefined }}>
      <div style={{ display:'flex', gap:8, marginBottom:20 }}>
        {[
          { key:'upcoming', label:`Próximos (${appointments.filter(a=>a.date>=today&&a.status!=='cancelled').length})` },
          { key:'past',     label:'Histórico' },
          { key:'all',      label:'Todos' },
        ].map(f => (
          <FilterChip key={f.key} label={f.label} active={filter===f.key} onClick={() => setFilter(f.key)} />
        ))}
        <div style={{ marginLeft:'auto' }}>
          <Button variant="primary" size="sm" onClick={() => navigate('/client/new-appointment')}>+ Agendar</Button>
        </div>
      </div>

      <div className="card">
        {filtered.length === 0 ? (
          <EmptyState icon={filter==='upcoming'?'📅':'📁'}
            title={filter==='upcoming' ? 'Sem agendamentos futuros' : 'Sem histórico'}
            action={filter==='upcoming' && <Button variant="primary" onClick={() => navigate('/client/new-appointment')}>+ Fazer Agendamento</Button>} />
        ) : (
          filtered.map(a => {
            const isPast = a.date < today;
            return (
              <div key={a.id} style={{ padding:'18px 20px', borderBottom:'1px solid var(--border)', display:'flex', gap:14, opacity: a.status==='cancelled'?0.6:1 }}>
                {/* Date badge */}
                <div style={{ width:52, flexShrink:0, textAlign:'center', background: isPast?'var(--surface-3)':'var(--surface-2)', borderRadius:10, padding:'8px 4px' }}>
                  <div style={{ fontSize:20, fontWeight:700, color: isPast?'var(--text-muted)':'var(--accent)', lineHeight:1 }}>
                    {a.date?.split('-')[2]}
                  </div>
                  <div style={{ fontSize:9, color:'var(--text-muted)', textTransform:'uppercase' }}>
                    {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'][Number(a.date?.split('-')[1])-1]}
                  </div>
                  <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{a.time}</div>
                </div>

                {/* Info */}
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8, marginBottom:6 }}>
                    <div>
                      <div style={{ fontWeight:600, fontSize:15 }}>{a.procedureName}</div>
                      <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:2 }}>
                        {a.professionalName} • {formatCurrency(a.price)}
                      </div>
                    </div>
                    <StatusBadge status={a.status} />
                  </div>

                  {/* Actions */}
                  {!isPast && a.status !== 'cancelled' && (
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginTop:8 }}>
                      {canConfirm(a) && (
                        <Button variant="success" size="sm" onClick={() => handleConfirm(a)}>✓ Confirmar presença</Button>
                      )}
                      {a.status === 'confirmed' && (
                        <span style={{ fontSize:13, color:'var(--success)', fontWeight:500, display:'flex', alignItems:'center', gap:4 }}>✓ Presença confirmada</span>
                      )}
                      {CHAT_ELIGIBLE.includes(a.status) && (
                        <Button variant="secondary" size="sm" onClick={() => navigate(`/client/chat/${a.id}`)}>💬 Chat</Button>
                      )}
                      {canCancel(a) && (
                        <Button variant="danger" size="sm" onClick={() => handleCancel(a)}>✗ Cancelar</Button>
                      )}
                    </div>
                  )}
                  {a.done && <span className="badge badge-success" style={{ marginTop:6, display:'inline-block' }}>✓ Atendimento realizado</span>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
